import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  FileText, Package, AlertTriangle, CheckCircle2, Download,
  Plus, Search, Filter, ChevronDown, ExternalLink, Trash2,
  TrendingUp, TrendingDown, Shield, Activity, Clock
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import {
  getSBOMReports,
  getSBOMStats,
  deleteSBOMReport,
  exportSBOMCycloneDX,
  exportSBOMSPDX,
  exportSBOMCSV,
  exportSBOMJSON,
  getHealthScoreColor,
  getHealthScoreBg,
  formatNumber,
  getEcosystemIcon,
  getEcosystemColor
} from '@/services/sbom';

const SBOMReportsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Determine base path from current location
  const basePath = location.pathname.startsWith('/litesabre') && !location.pathname.startsWith('/litesabre-pro') 
    ? '/litesabre' 
    : '/grc/litesabre';
  const [reports, setReports] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEcosystem, setSelectedEcosystem] = useState('');
  // Bulk selection + enrichment state (Feb 2026)
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const toggleSelectAll = (visibleIds) => {
    setSelectedIds((prev) => {
      const allSelected = visibleIds.every((id) => prev.has(id));
      const n = new Set(prev);
      if (allSelected) {
        visibleIds.forEach((id) => n.delete(id));
      } else {
        visibleIds.forEach((id) => n.add(id));
      }
      return n;
    });
  };

  const handleBulkEnrich = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Enrich ${selectedIds.size} SBOM report(s) for CISA 2026?\n\nEach report will:\n  • Get SBOM-level defaults auto-filled\n  • Have PURLs derived for every component\n  • Fetch real SHA-256 hashes from npm / PyPI / RubyGems\n  • Be signed with the platform SP key\n\nThis may take up to a minute for large SBOMs.`)) return;
    setBulkBusy(true);
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/litesabre/sbom/reports/bulk/enrich`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ sbom_ids: Array.from(selectedIds) }),
        },
      );
      const data = await r.json();
      toast.success(`Enriched ${data.ok_count}/${data.ok_count + data.error_count} SBOM(s)`);
      setSelectedIds(new Set());
      loadData();
    } catch (err) {
      toast.error('Bulk enrich failed');
    } finally {
      setBulkBusy(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [reportsData, statsData] = await Promise.all([
        getSBOMReports({ limit: 50 }),
        getSBOMStats()
      ]);
      setReports(reportsData.items || []);
      setStats(statsData);
    } catch (err) {
      console.error('Failed to load SBOM data:', err);
      toast.error('Failed to load SBOM reports');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (sbomId, e) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this SBOM report?')) return;
    
    try {
      await deleteSBOMReport(sbomId);
      toast.success('SBOM report deleted');
      loadData();
    } catch (err) {
      toast.error('Failed to delete report');
    }
  };

  const handleExport = async (sbomId, format, e) => {
    e.stopPropagation();
    try {
      switch (format) {
        case 'cyclonedx':
          await exportSBOMCycloneDX(sbomId);
          break;
        case 'spdx':
          await exportSBOMSPDX(sbomId);
          break;
        case 'csv':
          await exportSBOMCSV(sbomId);
          break;
        case 'json':
          await exportSBOMJSON(sbomId);
          break;
        default:
          break;
      }
      toast.success(`Exported as ${format.toUpperCase()}`);
    } catch (err) {
      toast.error('Export failed');
    }
  };

  const filteredReports = reports.filter(report => {
    const matchesSearch = report.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         report.target?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesEcosystem = !selectedEcosystem || 
                            (report.summary?.ecosystems && report.summary.ecosystems[selectedEcosystem]);
    return matchesSearch && matchesEcosystem;
  });

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="grid grid-cols-4 gap-4">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-28 bg-gray-200 rounded"></div>
            ))}
          </div>
          <div className="h-96 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <FileText className="w-7 h-7 text-indigo-600" />
            SBOM Reports
          </h1>
          <p className="text-gray-500 mt-1">
            Software Bill of Materials - Track dependencies, vulnerabilities, and package health
          </p>
        </div>
        <Button 
          onClick={() => navigate(`${basePath}/sbom/new`)} 
          className="bg-indigo-600 hover:bg-indigo-700"
          data-testid="generate-sbom-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Generate SBOM
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-l-4 border-l-indigo-500">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Reports</p>
                <p className="text-3xl font-bold">{stats?.total_reports || 0}</p>
              </div>
              <div className="p-3 bg-indigo-100 rounded-full">
                <FileText className="w-6 h-6 text-indigo-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Dependencies</p>
                <p className="text-3xl font-bold">{formatNumber(stats?.total_dependencies || 0)}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-full">
                <Package className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-red-500">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Critical Vulnerabilities</p>
                <p className="text-3xl font-bold text-red-600">{stats?.critical_vulnerabilities || 0}</p>
              </div>
              <div className="p-3 bg-red-100 rounded-full">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-green-500">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Avg Health Score</p>
                <p className={`text-3xl font-bold ${getHealthScoreColor(stats?.average_health_score || 0)}`}>
                  {stats?.average_health_score || 0}
                </p>
              </div>
              <div className={`p-3 rounded-full ${getHealthScoreBg(stats?.average_health_score || 0)}`}>
                <Activity className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Secondary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded">
                <TrendingDown className="w-4 h-4 text-orange-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500">Outdated</p>
                <p className="text-lg font-semibold">{stats?.outdated_packages || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded">
                <AlertTriangle className="w-4 h-4 text-red-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500">Deprecated</p>
                <p className="text-lg font-semibold">{stats?.deprecated_packages || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 rounded">
                <Shield className="w-4 h-4 text-yellow-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500">High Vulns</p>
                <p className="text-lg font-semibold">{stats?.high_vulnerabilities || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded">
                <TrendingUp className="w-4 h-4 text-purple-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500">Total Vulns</p>
                <p className="text-lg font-semibold">{stats?.total_vulnerabilities || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="py-4">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search by name or target..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="sbom-search-input"
                />
              </div>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="gap-2">
                  <Filter className="w-4 h-4" />
                  {selectedEcosystem || 'All Ecosystems'}
                  <ChevronDown className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem onClick={() => setSelectedEcosystem('')}>
                  All Ecosystems
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setSelectedEcosystem('npm')}>
                  {getEcosystemIcon('npm')} npm
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setSelectedEcosystem('pypi')}>
                  {getEcosystemIcon('pypi')} PyPI
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setSelectedEcosystem('maven')}>
                  {getEcosystemIcon('maven')} Maven
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setSelectedEcosystem('go')}>
                  {getEcosystemIcon('go')} Go
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setSelectedEcosystem('cargo')}>
                  {getEcosystemIcon('cargo')} Cargo
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </CardContent>
      </Card>

      {/* Reports Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">SBOM Reports</CardTitle>
          <CardDescription>
            {filteredReports.length} reports found
          </CardDescription>
        </CardHeader>
        <CardContent>
          {filteredReports.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500 mb-4">No SBOM reports found</p>
              <Button onClick={() => navigate(`${basePath}/sbom/new`)}>
                Generate Your First SBOM
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {/* Bulk action bar */}
              {filteredReports.length > 0 && (
                <div
                  className="sticky top-0 z-10 flex items-center justify-between px-3 py-2 border rounded-lg bg-slate-50"
                  data-testid="sbom-bulk-bar"
                >
                  <label className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer">
                    <input
                      type="checkbox"
                      className="w-4 h-4"
                      checked={filteredReports.length > 0 && filteredReports.every((r) => selectedIds.has(r.id))}
                      onChange={() => toggleSelectAll(filteredReports.map((r) => r.id))}
                      data-testid="sbom-bulk-select-all"
                    />
                    <span>
                      {selectedIds.size > 0
                        ? <><strong data-testid="sbom-bulk-count">{selectedIds.size}</strong> selected · </>
                        : 'Select all · '}
                      Enrich in bulk for CISA + G7 2026 (PURL · hashes · signature)
                    </span>
                  </label>
                  <div className="flex items-center gap-2">
                    {selectedIds.size > 0 && (
                      <Button size="sm" variant="ghost" onClick={() => setSelectedIds(new Set())} data-testid="sbom-bulk-clear">
                        Clear
                      </Button>
                    )}
                    <Button
                      size="sm"
                      disabled={bulkBusy || selectedIds.size === 0}
                      onClick={handleBulkEnrich}
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                      data-testid="sbom-bulk-enrich-btn"
                    >
                      {bulkBusy ? 'Enriching…' : `Enrich Selected (${selectedIds.size})`}
                    </Button>
                  </div>
                </div>
              )}

              {filteredReports.map((report) => (
                <div
                  key={report.id}
                  className="border rounded-lg p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                  onClick={() => navigate(`${basePath}/sbom/${report.id}`)}
                  data-testid={`sbom-report-${report.id}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <input
                          type="checkbox"
                          className="w-4 h-4"
                          checked={selectedIds.has(report.id)}
                          onClick={(e) => e.stopPropagation()}
                          onChange={() => toggleSelect(report.id)}
                          data-testid={`sbom-bulk-select-${report.id}`}
                        />
                        <h3 className="font-semibold text-lg">{report.name}</h3>
                        {report.summary?.ecosystems && Object.keys(report.summary.ecosystems).map(eco => (
                          <span 
                            key={eco} 
                            className={`text-xs px-2 py-1 rounded-full border ${getEcosystemColor(eco)}`}
                          >
                            {getEcosystemIcon(eco)} {eco}
                          </span>
                        ))}
                      </div>
                      <p className="text-sm text-gray-500 mb-3 truncate max-w-xl">{report.target}</p>
                      
                      <div className="flex flex-wrap gap-4 text-sm">
                        <div className="flex items-center gap-1">
                          <Package className="w-4 h-4 text-gray-400" />
                          <span>{report.summary?.total_dependencies || 0} dependencies</span>
                        </div>
                        
                        {(report.summary?.critical_vulnerabilities > 0 || report.summary?.high_vulnerabilities > 0) && (
                          <div className="flex items-center gap-2">
                            {report.summary?.critical_vulnerabilities > 0 && (
                              <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs font-medium">
                                {report.summary.critical_vulnerabilities} Critical
                              </span>
                            )}
                            {report.summary?.high_vulnerabilities > 0 && (
                              <span className="px-2 py-0.5 bg-orange-100 text-orange-700 rounded text-xs font-medium">
                                {report.summary.high_vulnerabilities} High
                              </span>
                            )}
                          </div>
                        )}
                        
                        <div className="flex items-center gap-1">
                          <Activity className="w-4 h-4 text-gray-400" />
                          <span className={getHealthScoreColor(report.summary?.average_health_score || 0)}>
                            Health: {Math.round(report.summary?.average_health_score || 0)}
                          </span>
                        </div>
                        
                        <div className="flex items-center gap-1 text-gray-400">
                          <Clock className="w-4 h-4" />
                          <span>{new Date(report.created_at).toLocaleDateString()}</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2 ml-4">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                          <Button variant="outline" size="sm" className="gap-1">
                            <Download className="w-4 h-4" />
                            Export
                            <ChevronDown className="w-3 h-3" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={(e) => handleExport(report.id, 'cyclonedx', e)}>
                            CycloneDX (JSON)
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={(e) => handleExport(report.id, 'spdx', e)}>
                            SPDX (JSON)
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={(e) => handleExport(report.id, 'csv', e)}>
                            CSV
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={(e) => handleExport(report.id, 'json', e)}>
                            Raw JSON
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                      
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        onClick={(e) => handleDelete(report.id, e)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SBOMReportsPage;
